import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ForecastModel, FORECAST_MODELS } from '@app/common/static-data/models';
import { VariableId } from '../types';
import { VARIABLES } from '@app/components/common/constants';

interface UseModelMissingVariableToastProps {
  modelsWithoutData: ForecastModel[];
  selectedModels: ForecastModel[];
  selectedParam: VariableId;
  hasAnyData: boolean;
  isLoading: boolean;
  forecastData: Record<string, any>; // Add forecast data to validate freshness
}

export function useModelMissingVariableToast({
  modelsWithoutData,
  selectedModels,
  selectedParam,
  hasAnyData,
  isLoading,
  forecastData,
}: UseModelMissingVariableToastProps) {
  const lastValidationRef = useRef<{
    selectedParam: VariableId;
    modelsWithoutData: ForecastModel[];
    timestamp: number;
  } | null>(null);

  useEffect(() => {
    // Don't show toast if loading or no data available
    if (isLoading || !hasAnyData) return;

    // Only show toast if some (but not all) models are missing data
    const shouldShowToast =
      modelsWithoutData.length > 0 && modelsWithoutData.length < selectedModels.length;

    if (!shouldShowToast) return;

    // RACE CONDITION FIX: Debounce toast notifications to prevent false positives
    // This prevents showing toasts when validation runs with stale data during variable changes
    const currentValidation = {
      selectedParam,
      modelsWithoutData: [...modelsWithoutData],
      timestamp: Date.now(),
    };

    // If this is the same validation as last time, and it's been stable for a bit, show toast
    const isSameValidation =
      lastValidationRef.current &&
      lastValidationRef.current.selectedParam === selectedParam &&
      JSON.stringify(lastValidationRef.current.modelsWithoutData) ===
        JSON.stringify(modelsWithoutData);

    const isStabilized =
      lastValidationRef.current &&
      currentValidation.timestamp - lastValidationRef.current.timestamp > 1000; // 1 second stability

    if (isSameValidation && isStabilized) {
      // Format model names for display
      const missingModelNames = modelsWithoutData
        .map((modelId) => FORECAST_MODELS.find((m) => m.id === modelId)?.label || modelId)
        .join(' and ');

      // Get variable display name
      const variable = VARIABLES.find((v) => v.id === selectedParam);
      const variableName = variable?.label || selectedParam;

      // Show toast notification
      toast.info(`${missingModelNames} currently do not support ${variableName.toLowerCase()}`, {
        duration: 4000,
        id: `missing-variable-${selectedParam}-${modelsWithoutData.join('-')}`,
      });
    }

    // Update the last validation
    lastValidationRef.current = currentValidation;
  }, [modelsWithoutData, selectedModels, selectedParam, hasAnyData, isLoading, forecastData]);
}
